import { areEqual, isValued } from './object';

/**
 * Determines whether a given object is an {@link Array}.
 *
 * @param toCheck the value to verify.
 *
 * @returns true if the given value is an {@link Array}, false otherwise.
 */
export function isArray(toCheck: any): boolean {
  return toCheck instanceof Array;
}

/**
 * Determines whether a given array has no values in it by evaluating its type, length and any <code>undefined</code>,
 * <code>null</code> values if needed. For simplicity of code, <code>undefined</code> and <code>null</code> values
 * are considered to be empty arrays, even though they aren't technically arrays.  Note this handling / definition of
 * <code>undefined</code> and <code>null</code> for the array is different from {@link isArray()}.
 *
 * @param toCheck the array to check for emptiness.
 * @param allowNullOrUndefinedValues set to true if the array can contain values that are <code>undefined</code> or
 * <code>null</code> and still be considered empty; default is false.
 *
 * @returns true if the given array is empty, null or undefined, false otherwise.
 */
export function isEmptyArray(toCheck: any[] | null | undefined, allowNullOrUndefinedValues: boolean = false): boolean {
  if (toCheck !== null && toCheck !== undefined && isArray(toCheck)) {
    if (allowNullOrUndefinedValues && toCheck.length > 0) {
      for (let i = 0; i < toCheck.length; i++) {
        if (isValued(toCheck[i])) {
          return false;
        }
      }

      return true;
    }

    return toCheck.length <= 0;
  }

  // Array is null, undefined or not an array object.
  return true;
}

/**
 * Determines whether a given array has values in it by evaluating its type, length and any <code>undefined</code>,
 * <code>null</code> values if needed. For simplicity of code, <code>undefined</code> and <code>null</code> values
 * are considered to be empty arrays, even though they aren't technically arrays.  Note this handling / definition of
 * <code>undefined</code> and <code>null</code> for the array is different from {@link isArray()}.  The
 * return value is the opposite result of {@link isEmptyArray()}.
 *
 * @param toCheck the array to check for emptiness.
 * @param allowNullOrUndefinedValues set to true if the array can contain values that are <code>undefined</code> or
 * <code>null</code> and still be considered empty; default is false.
 *
 * @returns true if the given value is an array and has at least one value, false otherwise.
 */
export function isNotEmptyArray(
  toCheck: any[] | null | undefined,
  allowNullOrUndefinedValues: boolean = false
): boolean {
  return !isEmptyArray(toCheck, allowNullOrUndefinedValues);
}

export function sortStringArray(arrayToSort: string[]): string[] {
  if (isNotEmptyArray(arrayToSort)) {
    return arrayToSort.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  return [];
}

/**
 * Determines if two arrays are equal, meaning they have the same number of elements and the elements contain the same
 * values.  The values must also be in the same orders, so they arrays may need to be sorted before calling into this
 * function.
 *
 * @param arrayA the first array for comparison.
 * @param arrayB the second array for comparison.
 *
 * @return true if the arrays are equal in element count and element values (ordered), false otherwise.
 */
export function areArraysEqual(arrayA: Array<unknown>, arrayB: Array<unknown>): boolean {
  if (arrayA.length !== arrayB.length) {
    return false;
  }
  for (let i = 0; i < arrayA.length; i++) {
    if (!areEqual(arrayA[i], arrayB[i])) {
      return false;
    }
  }

  return true;
}
